import logger from '@eventbrite/client-logger';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import Cookies from 'cookies-js';
import { WithSplitioNamespace } from '../state/splitio';
import {
    ENABLE_APPLE_PAY,
    ENABLE_INTERSTITIAL_STEP,
    ENABLE_NOS_DEMO,
    ENABLE_NOS_INTEGRATION,
} from '../utils/splitio';

const getOverridenFeatureFlags = () => {
    if (!HAS_WINDOW) {
        return {};
    }

    try {
        const signedFeatureFlagsOverride = Cookies.get('feature_flags_signed');

        const matches = /^data=([^&]+)&sig=/.exec(signedFeatureFlagsOverride);
        const base64FeatureFlagsOverride = matches?.[1];
        if (!base64FeatureFlagsOverride) {
            return {};
        }

        const featureFlagsOverrideString = atob(base64FeatureFlagsOverride);
        const featureFlagsOverride = JSON.parse(featureFlagsOverrideString);

        return Object.fromEntries(
            Object.entries(featureFlagsOverride).map(([name, state]) => [
                name,
                {
                    'overriden-treatment': {
                        treatment:
                            // We cannot import FeatureFlagState directly from @eventbrite/cypress-featureflags because
                            // it breaks TypeScript typechecker
                            state === 1 /* FeatureFlagState.DISABLED */
                                ? 'off'
                                : 'on',
                    },
                },
            ]),
        );
    } catch (error) {
        logger.warn(`Could not get Feature Flags override`, { error });
        return {};
    }
};

export const getSplitTreatmentValue = (
    state: WithSplitioNamespace,
    splitName: string,
): string => {
    const baseFeatureFlags = state.splitio?.treatments ?? {};
    const featureFlagsOverride = getOverridenFeatureFlags();
    const treatments = {
        ...baseFeatureFlags,
        ...featureFlagsOverride,
    };

    return (
        (treatments &&
            treatments[splitName] &&
            Object.values(treatments[splitName])?.[0]?.treatment) ??
        'off'
    );
};

export const isSplitTreatmentEnabled = (
    state: WithSplitioNamespace,
    splitName: string,
) => getSplitTreatmentValue(state, splitName) === 'on';

export const isApplePaySplitTreatmentEnabled = (state: WithSplitioNamespace) =>
    isSplitTreatmentEnabled(state, ENABLE_APPLE_PAY);

export const isLoginInterstitialSplitTreatmentEnabled = (
    state: WithSplitioNamespace,
) => isSplitTreatmentEnabled(state, ENABLE_INTERSTITIAL_STEP);

export const isNosDemoEnabled = (state: WithSplitioNamespace) =>
    isSplitTreatmentEnabled(state, ENABLE_NOS_DEMO);

export const isNosIntegrationEnabled = (state: WithSplitioNamespace) =>
    isSplitTreatmentEnabled(state, ENABLE_NOS_INTEGRATION);
